import { derived, get, writable } from "svelte/store";

import { regularizedPhone } from "@sourcewhale/shared/src/assets/js/src/general/utils";
import { SwDerivedApiStore } from "@sourcewhale/shared/src/svelte/api-stores/sw-api-store";
import { userStore } from "@sourcewhale/shared/src/svelte/api-stores/user-store";
import { swWritable } from "@sourcewhale/shared/src/svelte/utils/sw-writable";
import { tagsStore } from "../../../api-stores/tags-store";
import {
  fillFeedActivities,
  getContactRecords,
  getProfileRecords,
  getTrackingActivities,
} from "../../../api/activities";
import {
  getCandidate,
  getCandidateHistory,
  getProjects,
  getSearches,
} from "../../../api/candidates";
import { getFiles } from "../../../api/files";
import { flaggedContacts } from "./ProfileContactSideBar/stores";
import { extractGetSearchesEmails } from "./utils";

// Populate with an item in the array of responses from the todos query endpoint.
export const todosModalItemStore = writable(null);

export const showEditContact = writable(false);

// Keeps track of the active tab, useful for company insights
const ActiveTabStore = () => {
  const store = writable(null);

  // Make sure to reset the activeTabStore when the modal is closed
  todosModalItemStore.subscribe(($todo) => {
    if (!$todo) store.set(null);
  });

  return store;
};

export const activeTabStore = ActiveTabStore();

export const backgroundTabViewedStore = writable(false);

const candidateArgStore = derived(
  [todosModalItemStore],
  ([$todosModalItemStore]) => {
    if (!$todosModalItemStore) return null;
    const { candidateId, fromCache = false } = $todosModalItemStore;
    return candidateId ? [{ candidateId, fromCache }] : null;
  },
);

// BE returns null if candidate not found, but FE expects an object.
// Use a buffer store to catch this, then process in the real candidateStore below.
export const candidateApiStore = SwDerivedApiStore({
  get: getCandidate,
  argsStore: candidateArgStore,
  onMessage: ({ message, update, get: candidateGet }) => {
    if (message.kind !== "update-candidates") return;

    // Lock candidateStore when editing contact
    if (get(showEditContact)) return;

    // Multiple changes sent together can be related to this candidate
    message.changes.forEach((change) =>
      update((current) => {
        if (!current) return null; // Don't try to update if the candidate is null

        if (change.ids.includes(current.candidateId)) {
          // BE does some extra manipulation to phones and social links
          // so we need to do another api call if these are changed.
          if (change.update?.phones || change.update?.socialLinks) {
            candidateGet();
          }
          // return the changed candidate whether we've entered the above if block or not
          return { ...current, ...change.update };
        }

        return current;
      }),
    );
  },
});

export const candidateStore = derived(
  [candidateApiStore, todosModalItemStore],
  ([$candidateApiStore, $todosModalItemStore]) => {
    if (!$candidateApiStore) {
      if (!$todosModalItemStore) return {};

      // Return a reduced todosModalItemStore if the candidate is not found
      const { ...filteredItems } = $todosModalItemStore;
      return { ...filteredItems, isDeleted: true };
    }
    return $candidateApiStore;
  },
);

const crmCandidateArgStore = derived(
  [candidateArgStore, userStore],
  ([$candidateArgStore, $userStore]) =>
    $userStore.hasCrm ? $candidateArgStore : null,
);

export const candidateProjectsStore = SwDerivedApiStore({
  get: getProjects,
  argsStore: crmCandidateArgStore,
  argsTransform: (args) =>
    args && args[0]
      ? [
          {
            candidateIds: { projects: args.map((c) => c.candidateId) },
            contactModal: true,
          },
        ]
      : null,
  dataTransform: (data) => data?.projects || [],
});

const candidateHistoryArgStore = derived(
  [todosModalItemStore, backgroundTabViewedStore],
  ([$todosModalItemStore, $backgroundTabViewedStore]) => [
    $todosModalItemStore,
    $backgroundTabViewedStore,
  ],
);

export const candidateExperienceStore = SwDerivedApiStore({
  get: getCandidateHistory,
  argsStore: candidateHistoryArgStore,
  argsTransform: ([$todosModalItemStore, $backgroundTabViewedStore]) => {
    if (!$todosModalItemStore) return null;
    if (!$backgroundTabViewedStore) return null;
    const { candidateId } = $todosModalItemStore;
    return candidateId ? [candidateId, "experience"] : null;
  },
  dataTransform: (data) => data?.history?.experience || [],
});

export const candidateEducationStore = SwDerivedApiStore({
  get: getCandidateHistory,
  argsStore: candidateHistoryArgStore,
  argsTransform: ([$todosModalItemStore, $backgroundTabViewedStore]) => {
    if (!$todosModalItemStore) return null;
    if (!$backgroundTabViewedStore) return null;
    const { candidateId } = $todosModalItemStore;
    return candidateId ? [candidateId, "education"] : null;
  },
  dataTransform: (data) => data?.history?.education || [],
});

export const candidateFileIdsStore = derived(
  [candidateStore],
  ([$candidateStore]) => $candidateStore?.fileIds,
);

export const candidateFilesStore = SwDerivedApiStore({
  get: getFiles,
  argsStore: candidateFileIdsStore,
  argsTransform: (fileIds) =>
    fileIds ? [{ fileIds: fileIds.join(",") }] : null,
  dataTransform: (data) => data?.files || [],
});

export const avatarProps = derived(
  [todosModalItemStore, candidateStore],
  ([$todosModalItemStore, $candidateStore]) => {
    const cId1 = $todosModalItemStore?.candidateId;
    const cId2 = $candidateStore?.candidateId;
    const skeleton = !cId1 || !cId2 || cId1 !== cId2;

    return {
      src: $candidateStore.photo || "",
      firstName: $candidateStore?.firstName || "",
      lastName: $candidateStore?.lastName || "",
      skeleton,
    };
  },
);

const contactRecordsStoreArg = derived(
  [candidateStore, todosModalItemStore],
  ([$candidateStore, $todosModalItemStore]) => {
    const { candidateId, emails = [] } = $candidateStore;
    const cId = $todosModalItemStore?.candidateId;
    if (!cId || !candidateId || cId !== candidateId) return null;
    const items = emails.map((email) => ({ contact: email, candidateId }));
    return [{ items, useSavedCandidates: true }];
  },
);

export const contactRecordsStore = SwDerivedApiStore({
  get: getContactRecords,
  init: () => [],
  argsStore: contactRecordsStoreArg,
});

export const profileRecordsStore = SwDerivedApiStore({
  get: getProfileRecords,
  argsStore: todosModalItemStore,
  argsTransform: ($todosModalItemStore) => {
    if (!$todosModalItemStore) return null;
    const { candidateId } = $todosModalItemStore;
    return candidateId
      ? [{ candidateIds: [candidateId], current: true }]
      : null;
  },
});

export const candidatePhoneStore = derived(
  [candidateStore, profileRecordsStore, flaggedContacts],
  ([$candidateStore, $profileRecordsStore, $flaggedContacts]) => {
    const candidate = $candidateStore;
    if (!candidate) return [];
    const records = $profileRecordsStore;
    const record = records[candidate.candidateId] || {};

    // Combine candidate and profile records to get all phone numbers
    // - from least to best source.
    const phonesByNumber = {};

    [candidate, record].forEach((src) => {
      ["landline", "mobile", "other"].forEach((kind) => {
        const key = `${kind}Phones`;
        if (!src.phoneObjects || !src.phoneObjects[key]) return;
        src.phoneObjects[key].forEach((phone) => {
          const contact = regularizedPhone(phone.phone);
          if (!$flaggedContacts.includes(contact.trim())) {
            phonesByNumber[contact] = { ...phone, kind };
          }
        });
      });
    });
    return Object.values(phonesByNumber);
  },
);

export const callAnsweredStore = writable({});

const TodoPanelStore = () => {
  const store = swWritable({});

  let toDoId = null;

  todosModalItemStore.subscribe(($todosModalItemStore) => {
    if (!$todosModalItemStore) {
      store.set({}, true);
      return;
    }

    // Don't reset the store if the toDoId hasn't changed
    // Prevents e.g. phone call notes disappearing when websocket messages are received
    if (toDoId === $todosModalItemStore.toDoId) return;

    const { kind } = $todosModalItemStore;

    const init = () => {
      // initialise store with default values to ensure bindings don't change
      // dirty sub-store (see YourToDo.svelte)
      if (kind === "sendEmailAfterInmail") {
        return $todosModalItemStore.data.messageObj;
      }
      if (kind === "phoneCall") {
        return { phoneCallNote: "" };
      }
      return {};
    };

    store.set(init(), true);
    toDoId = $todosModalItemStore.toDoId;
  });

  return store;
};

export const todoPanelStore = TodoPanelStore();

export const selectedTableStore = writable({});

export const todoPanelTabs = derived(
  [todosModalItemStore, userStore],
  ([$todosModalItemStore, $userStore]) => {
    const tabItems = [
      { label: "Your To-do", slot: "yourToDoTab", onclick: () => {} },
      { label: "Contact Activity", slot: "activityTab", onclick: () => {} },
      {
        label: "Company Insights",
        slot: "companyInsightsTab",
        onclick: () => {},
      },
      { label: "Ask AI", slot: "askAiTab", onclick: () => {} },
      { label: "Background", slot: "backgroundTab", onclick: () => {} },
      { label: "Projects", slot: "projectsTab", onclick: () => {} },
      { label: "Files", slot: "filesTab", onclick: () => {} },
    ];
    const fromCache = $todosModalItemStore?.fromCache;

    return tabItems.filter((tab) => {
      const OMIT_YOUR_TODO = [
        "opportunity",
        "sendInmail",
        "linkedinConnectionRequest",
      ];
      const OMIT_COMPANY_INSIGHTS = ["sendInmail", "linkedinConnectionRequest"];

      // Hide "Your To-Do" for contact modal, opportunities and LinkedIn todos
      if (tab.slot === "yourToDoTab")
        return (
          $todosModalItemStore?.toDoId &&
          !OMIT_YOUR_TODO.includes($todosModalItemStore?.kind)
        );

      if (fromCache) return false; // only show "Your To-Do" tab if fromCache

      // Hide "Background", "Projects", and "Files" for users without Cetus license
      // Hide "Company Insights" for LinkedIn todos only
      // Show "Activity" and "Ask AI" for all todos
      const CRM_RESTRICTED_TABS = ["backgroundTab", "projectsTab", "filesTab"];
      return CRM_RESTRICTED_TABS.includes(tab.slot)
        ? $userStore.hasCrm
        : tab.slot !== "companyInsightsTab" ||
            !OMIT_COMPANY_INSIGHTS.includes($todosModalItemStore?.kind);
    });
  },
);

export const saveStatusPayloadStore = derived(
  [todosModalItemStore, todoPanelStore],
  ([$todosModalItemStore, $todoPanelStore]) => {
    const toDoIds = $todosModalItemStore ? [$todosModalItemStore.toDoId] : [];
    return { ...$todoPanelStore, toDoIds, kind: $todosModalItemStore?.kind };
  },
);

export const activityFeedStore = SwDerivedApiStore({
  get: fillFeedActivities,
  init: () => ({ feed: [] }),
  argsStore: candidateArgStore,
  argsTransform: (args) =>
    args ? [{ candidateId: args[0].candidateId }] : null,
});

const createTrackingStore = (activityType) =>
  SwDerivedApiStore({
    get: getTrackingActivities,
    init: () => [],
    argsStore: todosModalItemStore,
    argsTransform: ($todosModalItemStore) => {
      const { candidateId, toDoId, campaignId } = $todosModalItemStore || {};
      if (!candidateId) return null;
      const ret = { candidateId, kind: activityType };
      if (toDoId) ret.campaignId = campaignId; // only search for specific campaign on a todo
      return [ret];
    },
  });

export const clicksTrackingStore = createTrackingStore("click");
export const opensTrackingStore = createTrackingStore("open");

const createUniqueGroupedStore = (originalStore) => {
  const store = derived(originalStore, ($original) =>
    $original.reduce((acc, currentElement) => {
      const existingIndex = acc.findIndex(
        (item) =>
          item.campaignId === currentElement.campaignId &&
          item.stepNumber === currentElement.stepNumber,
      );

      if (existingIndex === -1) {
        acc.push({ ...currentElement, history: [], total: 1 });
      } else {
        acc[existingIndex].history.push(currentElement);
        acc[existingIndex].total = acc[existingIndex].history.length + 1;
      }

      return acc;
    }, []),
  );
  store.meta = originalStore.meta;
  return store;
};

export const opensGroupedStore = createUniqueGroupedStore(opensTrackingStore);
export const clicksGroupedStore = createUniqueGroupedStore(clicksTrackingStore);

// External email providers
//  - exclude Sourcewhale search for email and phone
//  - exclude kinds which only provide phone/mobile numbers
export const emailSearchKinds = derived(userStore, ($userStore) =>
  Object.entries($userStore?.searchKinds || {}).filter(
    ([kind]) =>
      !["email", "phone"].includes(kind) &&
      !kind.endsWith("--phone") &&
      !kind.endsWith("--mobile"),
  ),
);

export const lastEnrichTime = writable(null);
const suggestionsArgs = derived(
  [todosModalItemStore, lastEnrichTime],
  ([$todosModalItemStore, $lastEnrichTime]) => {
    if (
      !$todosModalItemStore?.candidateId ||
      $todosModalItemStore.kind !== "bounce"
    )
      return null;

    const searchParams = {
      resultIdsMap: { [$todosModalItemStore.candidateId]: ["all"] },
      checkBounces: true,
    };
    if (lastEnrichTime) {
      searchParams.timestamp = $lastEnrichTime;
    }

    return [searchParams];
  },
);

export const suggestionsStore = SwDerivedApiStore({
  get: getSearches,
  argsStore: suggestionsArgs,
  dataTransform: (data) => extractGetSearchesEmails(data),
});

// Editable fields shown in the edit contact section of the modal
export const editContactSections = [
  {
    header: "Personal Contact",
    items: [
      { label: "Name", id: "firstName", type: "text" },
      { label: "Last Name", id: "lastName", type: "text" },
      { label: "Email", id: "emails", type: "chip" },
      { label: "Phone", id: "phones", type: "chip" },
      { label: "Social Links", id: "socialLinks", type: "chip" },
    ],
    kind: "default",
  },
  {
    header: "Role",
    items: [
      { label: "Job Title", id: "role", type: "text" },
      { label: "Company", id: "company", type: "companyAutocomplete" },
      {
        label: "Company Relationship",
        id: "relationship",
        type: "relationship",
        restriction: "hasCrm",
      },
    ],
    kind: "default",
  },
  {
    header: "Salary", // all these are restricted to hasCrm users
    items: [
      // some types are null as those items render with the preceding item(s) in a shared component
      {
        label: "Salary Currency",
        id: "salaryCurrency",
        type: "currencyDropdown",
      },
      {
        label: "Notice Period (months)",
        id: "noticePeriod",
        type: "text",
      },
      { label: "Current Salary", id: "salary", type: "currentSalary" },
      { label: "Current Bonus", id: "currentBonus" },
      {
        label: "Expected Salary (min)",
        id: "expectedSalaryMin",
        type: "expectedSalary",
      },
      { label: "Expected Salary (max)", id: "expectedSalaryMax" },
      { label: "Expected Bonus (min)", id: "expectedBonusMin" },
      { label: "Expected Bonus (max)", id: "expectedBonusMax" },
    ],
    kind: "salary",
    restriction: "hasCrm",
  },
  {
    header: "Address",
    items: [
      { label: "City", id: "city", type: "text" },
      { label: "County / State", id: "state", type: "text" },
      { label: "Country", id: "country", type: "text" },
    ],
    kind: "default",
  },
  {
    header: "Other",
    items: [
      { label: "Tags", id: "tagIds", type: "tags" },
      { label: "Comments", id: "comments", type: "freeText" },
    ],
    kind: "default",
  },
];

const filteredCandidateStore = derived(
  [candidateStore, tagsStore],
  ([$candidateStore, $tagsStore]) => {
    const tagIds = $tagsStore.map((tag) => tag.tagId);
    if ($candidateStore.tagIds) {
      $candidateStore.tagIds = $candidateStore.tagIds.filter((tagId) =>
        tagIds.includes(tagId),
      );
    }
    return $candidateStore;
  },
);

export const candidateTagStore = derived(
  [filteredCandidateStore, tagsStore],
  ([$filteredCandidateStore, $tagsStore]) =>
    ($filteredCandidateStore.tagIds || []).map((x) => ({
      id: x,
      title: $tagsStore.find((tag) => tag.tagId === x).title,
    })),
);

const EditContactStore = () => {
  const store = swWritable({});
  let defaultsStr = "{}";

  showEditContact.subscribe(($showEditContact) => {
    if (!$showEditContact) {
      store.set(JSON.parse(defaultsStr), true);
    }
  });
  // For comparing with the candidate store (allows dirty checking)
  // Subscribed to showEditContact to reset the store when the modal is closed
  filteredCandidateStore.subscribe((candidate) => {
    // prevent reset of store when editing contact
    // "update-to-dos" refreshes todosQuery.data (onMessage) even if no change
    // this triggers refresh of todosModalItemStore via todosModalInterface $effect
    // which propagates here
    if (get(showEditContact)) return;

    const candidateProperties = {};
    editContactSections.forEach((section) => {
      section.items.forEach((item) => {
        candidateProperties[item.id] =
          candidate[item.id] ||
          (["chip", "tags"].includes(item.type) ? [] : "");
      });
    });
    defaultsStr = JSON.stringify(candidateProperties);
    store.set(candidateProperties, true);
  });
  return store;
};

export const editContactStore = EditContactStore();

export const modalBodyHeight = writable(0);
export const compactModal = derived(
  [modalBodyHeight],
  ([$modalBodyHeight]) => $modalBodyHeight < 538,
);

export const jobInfo = derived(
  [todosModalItemStore, candidateStore, candidateApiStore.meta],
  ([$todosModalItemStore, $candidateStore, $meta]) => {
    if (!$todosModalItemStore?.candidateId) return {};
    const skeleton = $meta?.fetching;
    if ($candidateStore?.candidateId === $todosModalItemStore?.candidateId) {
      return {
        skeleton,
        candidateRole:
          $candidateStore?.role || $todosModalItemStore?.candidateRole || "",
        candidateCompany:
          $candidateStore?.company ||
          $todosModalItemStore?.candidateCompany ||
          "",
        candidateName:
          $candidateStore?.name || $todosModalItemStore?.candidateName || "",
        candidateLocation:
          $candidateStore?.location ||
          $todosModalItemStore?.candidateLocation ||
          "",
        timezone: $candidateStore?.timezone,
        companyId: $candidateStore?.companyId,
        relationship: $candidateStore?.relationship,
      };
    }
    const {
      candidateRole = "",
      candidateCompany = "",
      candidateLocation = "",
      candidateName = "",
    } = $todosModalItemStore || {};
    return {
      skeleton,
      candidateRole,
      candidateCompany,
      candidateLocation,
      candidateName,
    };
  },
);

export const candidateSourceStore = derived(
  candidateStore,
  ($candidateStore) => ({
    source: $candidateStore.source,
    sourceName: $candidateStore.sourceName,
  }),
);

export const activePopUpModalStore = writable(null);

export const disableAddToContactStore = writable(false);
